import { useState } from "react";
import type { FormEvent } from "react";
import { GameWordmark } from "../components/GameWordmark.js";
import { sendMagicLinkEmail } from "../api/auth.js";

interface Props {
  onBack: () => void;
}

type Stage = "idle" | "sending" | "sent" | "error";

export function LoginGame({ onBack }: Props) {
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState<Stage>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || stage === "sending") return;
    setStage("sending");
    setErrorMessage("");
    const res = await sendMagicLinkEmail(email.trim());
    if (res.ok) {
      setStage("sent");
    } else {
      setStage("error");
      setErrorMessage(res.message || "Could not send the email. Try again.");
    }
  };

  return (
    <div className="ghero screen-enter">
      <div className="wrap ghero-inner">
        <div className="ghero-nav pop">
          <GameWordmark size={24} />
          <div className="ghero-nav-right">
            <button className="ghero-nav-link" onClick={onBack}>← Back</button>
          </div>
        </div>

        <div className="ghero-hero">
          <div className="ghero-left">
            <span className="pill pill--yellow ghero-kicker pop">★ Player select</span>
            <h1 className="gdisplay ghero-h1 pop" style={{ animationDelay: ".05s" }}>
              Insert email.<br />
              <span className="ghero-h1-red">Continue.</span>
            </h1>

            {stage !== "sent" && (
              <>
                <p className="ghero-sub pop" style={{ animationDelay: ".12s" }}>
                  Drop your email. We mail you a one-tap sign-in link. No passwords. No "sign up vs sign in." No bullshit.
                </p>

                <form className="ghero-form pop" style={{ animationDelay: ".18s" }} onSubmit={submit}>
                  <div className="ghero-input gcard">
                    <span className="ghero-proto gpixel">@</span>
                    <input
                      className="ghero-field"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      spellCheck={false}
                      placeholder="you@yourdomain.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={stage === "sending"}
                    />
                    <button
                      type="submit"
                      className="gbtn gbtn--primary gbtn--lg ghero-go"
                      disabled={!valid || stage === "sending"}
                    >
                      {stage === "sending" ? "Sending…" : "Send the link"}{" "}
                      <span aria-hidden="true">&rarr;</span>
                    </button>
                  </div>
                  <p className="glabel ghero-warn">
                    {stage === "error"
                      ? errorMessage
                      : "Link is good for 15 minutes. One tap, one use."}
                  </p>
                </form>
              </>
            )}

            {stage === "sent" && (
              <>
                <p className="ghero-sub pop" style={{ animationDelay: ".12s" }}>
                  We just sent a one-tap sign-in link. Open it from your inbox on this device, and you're in. The link is good for 15 minutes.
                </p>

                <div className="login-sent gcard pop" style={{ animationDelay: ".18s" }}>
                  <span className="login-sent-icon" aria-hidden="true">✓</span>
                  <div className="login-sent-body">
                    <span className="login-sent-label">Magic link delivered to</span>
                    <span className="login-sent-email">{email.trim()}</span>
                  </div>
                </div>

                <p className="glabel ghero-warn pop" style={{ animationDelay: ".22s", color: "var(--ink-soft)" }}>
                  No email after a minute? Check spam, then{" "}
                  <button
                    type="button"
                    className="login-retry"
                    onClick={() => {
                      setStage("idle");
                      setEmail("");
                    }}
                  >
                    use a different email
                  </button>
                  .
                </p>
              </>
            )}
          </div>

          <div className="ghero-right pop" style={{ animationDelay: ".24s" }}>
            <div className="ghero-card gcard">
              <span className="glabel">Why sign in</span>
              <ul style={{ margin: "18px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 14 }}>
                <li>★ Save your scores and re-play sites over time</li>
                <li>★ Pro tier: 8-page crawl per scan, full lens history</li>
                <li>★ One inbox magic link. Zero passwords stored.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
